import React from 'react'
import { ShieldCheckIcon, LockClosedIcon, EyeIcon, UserGroupIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import SEO from '@components/common/SEO'

const PrivacyPage: React.FC = () => {
  const lastUpdated = '1 ตุลาคม 2025'

  const privacyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "นโยบายความเป็นส่วนตัว - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "นโยบายความเป็นส่วนตัวและการคุ้มครองข้อมูลส่วนบุคคลของไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "dateModified": "2025-10-01"
  }

  return (
    <>
      <SEO
        title="นโยบายความเป็นส่วนตัว"
        description="นโยบายความเป็นส่วนตัวและการคุ้มครองข้อมูลส่วนบุคคลของไทยเซ็กคิวริตี้ไดเรกทอรี่ - เราให้ความสำคัญกับความปลอดภัยของข้อมูลของคุณ"
        keywords="นโยบายความเป็นส่วนตัว, privacy policy, PDPA, ข้อมูลส่วนบุคคล"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(privacyStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <ShieldCheckIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                นโยบายความเป็นส่วนตัว
              </h1>
              <p className="text-xl text-primary-100">
                เราให้ความสำคัญกับความเป็นส่วนตัวและความปลอดภัยของข้อมูลของคุณ
              </p>
              <p className="text-sm text-primary-200 mt-4">
                อัปเดตล่าสุด: {lastUpdated}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container-wrapper py-12">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                ไทยเซ็กคิวริตี้ไดเรกทอรี่ ("เรา" "พวกเรา" หรือ "บริษัท") ให้ความสำคัญกับความเป็นส่วนตัวของผู้ใช้งานทุกท่าน
                นโยบายความเป็นส่วนตัวฉบับนี้อธิบายวิธีการที่เราเก็บรวบรวม ใช้ เปิดเผย และคุ้มครองข้อมูลส่วนบุคคลของคุณ
                เมื่อคุณใช้บริการของเรา โปรดอ่านนโยบายนี้อย่างละเอียดเพื่อทำความเข้าใจสิทธิและหน้าที่ของคุณ
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-8">
              {/* Section 1 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <DocumentTextIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      1. ข้อมูลที่เราเก็บรวบรวม
                    </h2>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">1.1 ข้อมูลที่คุณให้โดยตรง</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>ข้อมูลการสมัครสมาชิก: ชื่อ-นามสกุล, อีเมล, เบอร์โทรศัพท์, รหัสผ่าน</li>
                      <li>ข้อมูลการ Claim บริษัท: ข้อมูลบริษัท, เอกสารยืนยันตัวตน (บัตรประชาชน, ใบทะเบียนบริษัท)</li>
                      <li>ข้อมูลการติดต่อ: ข้อความ, อีเมล, เบอร์โทรที่ใช้ติดต่อเรา</li>
                      <li>ข้อมูลการชำระเงิน: ข้อมูลบัตรเครดิต/เดบิต (ถ้ามีการใช้บริการเสียเงินในอนาคต)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">1.2 ข้อมูลที่เก็บอัตโนมัติ</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>ข้อมูลการใช้งาน: หน้าที่เข้าชม, เวลา, ระยะเวลาการเข้าชม</li>
                      <li>ข้อมูลเทคนิค: IP Address, ประเภทเบราว์เซอร์, ระบบปฏิบัติการ, ข้อมูลอุปกรณ์</li>
                      <li>Cookies และเทคโนโลยีคล้ายกัน: เพื่อปรับปรุงประสบการณ์การใช้งาน</li>
                      <li>ข้อมูลตำแหน่งที่ตั้ง: เพื่อแสดงผลค้นหาที่เกี่ยวข้องกับพื้นที่ของคุณ</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <EyeIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      2. วัตถุประสงค์ในการใช้ข้อมูล
                    </h2>
                  </div>
                </div>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <p>เราใช้ข้อมูลของคุณเพื่อ:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>ให้บริการค้นหาและแสดงข้อมูลบริษัทรักษาความปลอดภัย</li>
                    <li>จัดการบัญชีผู้ใช้และการยืนยันตัวตน</li>
                    <li>ตรวจสอบและอนุมัติการ Claim บริษัท</li>
                    <li>ส่งการแจ้งเตือนและข้อมูลเกี่ยวกับบริการ</li>
                    <li>ปรับปรุงและพัฒนาบริการให้ดีขึ้น</li>
                    <li>วิเคราะห์พฤติกรรมการใช้งานเพื่อปรับปรุง UX/UI</li>
                    <li>ป้องกันการฉอกโกง และรักษาความปลอดภัยของระบบ</li>
                    <li>ปฏิบัติตามกฎหมายและข้อบังคับที่เกี่ยวข้อง</li>
                  </ul>
                </div>
              </div>

              {/* Section 3 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <UserGroupIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      3. การแชร์และเปิดเผยข้อมูล
                    </h2>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>เราจะไม่ขายหรือเช่าข้อมูลส่วนบุคคลของคุณให้กับบุคคลที่สาม แต่อาจแชร์ข้อมูลในกรณีต่อไปนี้:</p>

                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">3.1 การแชร์ภายในแพลตฟอร์ม</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>ข้อมูลบริษัทที่คุณ Claim จะแสดงต่อสมาชิกที่ลงทะเบียน (ตามระดับสิทธิ์)</li>
                      <li>ข้อมูลติดต่อพื้นฐาน (ชื่อ, บริษัท) แสดงต่อสมาชิก MEMBER ที่ต้องการติดต่อ</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">3.2 ผู้ให้บริการภายนอก (Third Parties)</h3>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>ผู้ให้บริการโฮสติ้งและคลาวด์ (AWS, Google Cloud)</li>
                      <li>ผู้ให้บริการชำระเงิน (Stripe, PayPal - ในอนาคต)</li>
                      <li>ผู้ให้บริการวิเคราะห์ข้อมูล (Google Analytics)</li>
                      <li>ผู้ให้บริการอีเมล (SendGrid, Amazon SES)</li>
                    </ul>
                    <p className="text-sm italic mt-2">* ผู้ให้บริการเหล่านี้ถูกผูกพันด้วยสัญญาคุ้มครองข้อมูล</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">3.3 กรณีตามกฎหมาย</h3>
                    <p>เราอาจเปิดเผยข้อมูลเมื่อ:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>มีคำสั่งศาลหรือหน่วยงานราชการที่มีอำนาจ</li>
                      <li>เพื่อป้องกันหรือสืบสวนการฉอกโกง, การละเมิดกฎหมาย</li>
                      <li>เพื่อปกป้องสิทธิ์, ทรัพย์สิน, หรือความปลอดภัยของเราและผู้ใช้</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <LockClosedIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      4. ความปลอดภัยของข้อมูล
                    </h2>
                  </div>
                </div>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <p>เราใช้มาตรการรักษาความปลอดภัยระดับมาตรฐานอุตสาหกรรม:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>การเข้ารหัส:</strong> ข้อมูลทั้งหมดเข้ารหัสด้วย SSL/TLS (HTTPS)</li>
                    <li><strong>การเข้ารหัสรหัสผ่าน:</strong> ใช้ bcrypt hash algorithm</li>
                    <li><strong>การควบคุมการเข้าถึง:</strong> จำกัดการเข้าถึงข้อมูลตามบทบาทและความจำเป็น</li>
                    <li><strong>การตรวจสอบความปลอดภัย:</strong> ตรวจสอบและอัปเดตระบบสม่ำเสมอ</li>
                    <li><strong>Backup:</strong> สำรองข้อมูลอัตโนมัติเพื่อป้องกันการสูญหาย</li>
                    <li><strong>Rate Limiting:</strong> ป้องกัน DDoS และการใช้งานผิดปกติ</li>
                  </ul>
                  <p className="text-sm italic mt-4 text-amber-700">
                    แม้เราจะใช้มาตรการรักษาความปลอดภัยที่เหมาะสม แต่ไม่มีระบบใดที่ปลอดภัย 100%
                    กรุณาช่วยดูแลรักษาข้อมูลบัญชีของคุณและไม่แชร์รหัสผ่านกับผู้อื่น
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      5. สิทธิ์ของเจ้าของข้อมูล (ตาม PDPA)
                    </h2>
                  </div>
                </div>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <p>คุณมีสิทธิ์ตามกฎหมาย PDPA ดังนี้:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>สิทธิ์ในการเข้าถึง:</strong> ขอเข้าถึงและขอรับสำเนาข้อมูลส่วนบุคคล</li>
                    <li><strong>สิทธิ์ในการแก้ไข:</strong> แก้ไขข้อมูลที่ไม่ถูกต้องหรือไม่สมบูรณ์</li>
                    <li><strong>สิทธิ์ในการลบ:</strong> ขอลบข้อมูลส่วนบุคคล (ยกเว้นกรณีที่กฎหมายกำหนด)</li>
                    <li><strong>สิทธิ์ในการระงับ:</strong> ระงับการใช้ข้อมูลชั่วคระว</li>
                    <li><strong>สิทธิ์ในการโอนย้าย:</strong> ขอให้โอนข้อมูลไปยังผู้ควบคุมข้อมูลอื่น</li>
                    <li><strong>สิทธิ์ในการคัดค้าน:</strong> คัดค้านการประมวลผลข้อมูล</li>
                    <li><strong>สิทธิ์ในการถอนความยินยอม:</strong> ถอนความยินยอมได้ทุกเมื่อ</li>
                  </ul>
                  <p className="mt-4">
                    <strong>วิธีการใช้สิทธิ์:</strong> ติดต่อมาที่ <a href="mailto:info@koder3.com" className="text-primary-600 hover:text-primary-700 underline">info@koder3.com</a>
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  6. Cookies และเทคโนโลยีติดตาม
                </h2>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <p>เราใช้ Cookies และเทคโนโลยีคล้ายกันเพื่อ:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>จดจำการเข้าสู่ระบบและการตั้งค่า</li>
                    <li>วิเคราะห์การใช้งานและปรับปรุงบริการ</li>
                    <li>แสดงเนื้อหาที่เหมาะสมกับคุณ</li>
                  </ul>
                  <p className="mt-4">
                    คุณสามารถจัดการ Cookies ได้ผ่านการตั้งค่าเบราว์เซอร์ แต่การปิด Cookies อาจส่งผลต่อการใช้งานบางฟีเจอร์
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  7. ระยะเวลาการเก็บรักษาข้อมูล
                </h2>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>ข้อมูลบัญชี:</strong> เก็บไว้ตลอดอายุของบัญชี + 2 ปีหลังการลบบัญชี</li>
                    <li><strong>ข้อมูล Log:</strong> เก็บไว้ 1 ปี เพื่อความปลอดภัยและการวิเคราะห์</li>
                    <li><strong>ข้อมูลการทำธุรกรรม:</strong> เก็บไว้ 10 ปี ตามกฎหมายภาษี</li>
                    <li><strong>ข้อมูลการติดต่อ:</strong> เก็บไว้ 3 ปี หรือจนกว่าคุณจะขอลบ</li>
                  </ul>
                </div>
              </div>

              {/* Section 8 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  8. การเปลี่ยนแปลงนโยบาย
                </h2>

                <div className="text-neutral-700 leading-relaxed">
                  <p>
                    เราอาจปรับปรุงนโยบายความเป็นส่วนตัวนี้เป็นครั้งคราว การเปลี่ยนแปลงสำคัญจะแจ้งให้ทราบผ่านอีเมลหรือประกาศบนเว็บไซต์
                    กรุณาตรวจสอบนโยบายนี้เป็นระยะ วันที่อัปเดตล่าสุดจะแสดงไว้ด้านบน
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  9. การติดต่อเรา
                </h2>

                <div className="text-neutral-700 leading-relaxed space-y-3">
                  <p>
                    หากคุณมีคำถามเกี่ยวกับนโยบายความเป็นส่วนตัวนี้ หรือต้องการใช้สิทธิ์ของคุณ กรุณาติดต่อ:
                  </p>
                  <div className="bg-neutral-50 p-4 rounded-lg">
                    <p><strong>ไทยเซ็กคิวริตี้ไดเรกทอรี่</strong></p>
                    <p className="mt-2">
                      <strong>อีเมล:</strong> <a href="mailto:info@koder3.com" className="text-primary-600 hover:text-primary-700 underline">info@koder3.com</a>
                    </p>
                    <p>
                      <strong>เว็บไซต์:</strong> <a href="https://thaisecurity.directory" className="text-primary-600 hover:text-primary-700 underline">https://thaisecurity.directory</a>
                    </p>
                    <p className="mt-2 text-sm text-neutral-600">
                      เราจะตอบกลับคำขอของคุณภายใน 30 วัน
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white text-center">
              <h2 className="text-2xl font-bold mb-3">
                มีคำถามเกี่ยวกับความเป็นส่วนตัว?
              </h2>
              <p className="text-primary-100 mb-6">
                ติดต่อทีมงานของเราได้ตลอดเวลา เรายินดีให้คำชี้แจง
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors duration-200 shadow-md"
                >
                  ติดต่อเรา
                </a>
                <a
                  href="/faq"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-400 transition-colors duration-200"
                >
                  คำถามที่พบบ่อย
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PrivacyPage
